import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule } from '@angular/core';
import { webSocket } from 'rxjs/webSocket';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

const WEB_SOCKET_CONFIG = new InjectionToken('WEB_SOCKET_CONFIG');

class WebSocketService {
    constructor(webSocketConfig) {
        this.webSocketConfig = webSocketConfig;
        this.baseUrl = '';
        this.isConnected$ = new BehaviorSubject(false);
        this.reconnectTimes = 0;
        if (this.webSocketConfig) {
            this.baseUrl = this.webSocketConfig.baseUrl;
        }
    }
    connect(token, topic) {
        console.log('calling connect()');
        this.topic = topic;
        if (this.myWebSocket &&
            !this.myWebSocket?.closed &&
            token === this.currentToken) {
            this.myWebSocket.pipe(take(1)).subscribe();
            console.log('Active Websocket Subject - resubscribing');
            return;
        }
        //debugging logs, can be used to determine why a duplicate connection occured
        if (!this.myWebSocket)
            console.log('Websocket not created yet');
        if (this.myWebSocket?.closed)
            console.log('Websocket closed, will re-create');
        if (token !== this.currentToken)
            console.log('Websocket received new token');
        console.log('Starting a new Websocket');
        this.currentToken = token;
        this.myWebSocket = webSocket({
            url: `${this.baseUrl}?authType=token`,
            protocol: [token],
            openObserver: {
                next: () => {
                    this.isConnected$.next(true);
                    this.reconnectTimes = 0;
                    console.log('Websocket Connected');
                    // need to ping aws websocket gateway to keep connection alive every 30 seconds
                    // if connection is established, we will reset interval
                    this.keepWebsocketAlive();
                    // AWS Will drop connection after 2 hours
                    // So we are reconnecting always after 1 hour 50 minutes
                    // TODO: rethink logic around this
                    this.forceReconnectInterval = setInterval(() => {
                        console.log('in forceReconnectInterval: calling close');
                        this.close();
                    }, WebSocketService.FORCE_RECONNECT);
                    this.myWebSocket?.subscribe((msg) => {
                        if (msg?.data) {
                            try {
                                const messageData = JSON.parse(msg?.data);
                                if (messageData?.eventAction === 'RegisterationAction') {
                                    const eventTypes = messageData?.eventType?.join(', ');
                                    console.log(`%c** ${messageData?.namespace?.toLowerCase()} domain is connected and listening for the following event types:  [${eventTypes}] **`, 'color: green; font-weight:bold;');
                                }
                            }
                            catch (e) {
                                console.log('catching error in myWebSocket.subscribe: ', e);
                            }
                        }
                    }, (err) => {
                        console.error(`websocket connection error at ${new Date().toISOString()}:  `, err);
                    }, () => {
                        console.log(`%c** websocket was disconnected at ${new Date().toISOString()}**`, 'color: red; font-weight:bold;');
                    });
                },
            },
            closeObserver: {
                next: (e) => {
                    this.isConnected$.next(false);
                    console.log(`Websocket Error Code = ${e.code}`);
                    // IF TOKEN IS STILL VALID AND USER HAS NOT LOGGED OUT,
                    // ATTEMPT TO RECONNECT THE WEBSOCKET
                    if (e.code !== 3000) {
                        // reconnect
                        this.resetAndConnect(token);
                    }
                },
            },
            deserializer: (msg) => msg,
            serializer: (msg) => JSON.stringify({ action: this.topic, message: msg.data }),
        });
        this.myWebSocket?.pipe(take(1)).subscribe();
    }
    getTopicData() {
        if (!this.myWebSocket) {
            return;
        }
        return this.myWebSocket?.asObservable();
    }
    sendMsg(message) {
        const myMessage = new MessageEvent('worker', {
            data: message,
        });
        this.myWebSocket?.next(myMessage);
    }
    register(eventsToRegister) {
        eventsToRegister.forEach((event) => {
            const myMessage = new MessageEvent('worker', {
                data: event,
            });
            this.myWebSocket.next(myMessage);
        });
    }
    keepWebsocketAlive() {
        clearInterval(this.keepAliveInterval);
        this.keepAliveInterval = setInterval(() => {
            this.topic = 'OnKeepAlive';
            this.sendMsg('{"message": ""}');
            this.monitorHealthStatus();
        }, WebSocketService.KEEP_ALIVE_INTERVAL);
    }
    close() {
        console.log('calling close()');
        this.myWebSocket.complete();
    }
    closeConnection() {
        console.log('calling closeConnection()');
        if (!this.myWebSocket || !this.isConnected$.getValue()) {
            return;
        }
        this.myWebSocket.error({ code: 3000, reason: 'User has logged out.' });
        clearInterval(this.keepAliveInterval);
        clearInterval(this.healthInterval);
        clearInterval(this.forceReconnectInterval);
    }
    resetAndConnect(token) {
        clearInterval(this.keepAliveInterval);
        clearInterval(this.healthInterval);
        clearInterval(this.forceReconnectInterval);
        this.increaseCount();
        if (this.getCount() > WebSocketService.MAX_RETRIES) {
            throw Error('Websocket Connection Error:  Too many connection retries.');
        }
        else {
            setTimeout(() => {
                console.log(`reconnecting websocket.. ${new Date().toISOString()}`);
                this.connect(token, 'OnRegistration');
            }, WebSocketService.RETRY_DELAY * this.getCount());
        }
    }
    increaseCount() {
        this.reconnectTimes += 1;
    }
    getCount() {
        return this.reconnectTimes;
    }
    resetCount() {
        this.reconnectTimes = 0;
    }
    monitorHealthStatus() {
        clearInterval(this.healthInterval);
        // need to make sure we receive a response from aws websocket gateway within 60 seconds
        this.healthInterval = setInterval(() => {
            console.log('in monitorHealthStatus: calling close');
            this.close();
        }, WebSocketService.HEALTH_INTERVAL);
    }
    closeConnectionWithDelay(delayPeriod) {
        this.delayCloseTimer = setTimeout(() => {
            console.log('[Lifecycle] Closing Websocket');
            this.closeConnection();
        }, delayPeriod);
    }
    cancelDelay() {
        clearTimeout(this.delayCloseTimer);
    }
}
WebSocketService.KEEP_ALIVE_INTERVAL = 30000;
WebSocketService.HEALTH_INTERVAL = 60000;
WebSocketService.FORCE_RECONNECT = 6600000;
WebSocketService.MAX_RETRIES = 10;
WebSocketService.RETRY_DELAY = 1000;
WebSocketService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WebSocketService, deps: [{ token: WEB_SOCKET_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
WebSocketService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WebSocketService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WebSocketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [WEB_SOCKET_CONFIG]
                }] }]; } });

class WebSocketModule {
    static forRoot(webSocketConfig) {
        return {
            ngModule: WebSocketModule,
            providers: [
                {
                    provide: WEB_SOCKET_CONFIG,
                    useValue: webSocketConfig
                },
                WebSocketService
            ]
        };
    }
}
WebSocketModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WebSocketModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
WebSocketModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: WebSocketModule });
WebSocketModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WebSocketModule, providers: [WebSocketService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WebSocketModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                    providers: [WebSocketService]
                }]
        }] });

/*
 * Public API Surface of websocket
 */

/**
 * Generated bundle index. Do not edit.
 */

export { WebSocketModule, WebSocketService };
